import { useId } from 'react';

import type { AIModelEntry, PeriodBounds } from '@/lib/api/generated/model';
import { cn } from '@/lib/utils';
import { CompositionBar } from '@/shared/components/CompositionBar';
import { SectionHeading } from '@/shared/components/SectionHeading';
import {
  shareSegments,
  summaryLine,
  type ProfileIdentity,
} from '../modelsLedger';
import { identityDot, quantityFill } from '../identityFill';

/**
 * Which profile does the work (spec 233 US1): one bar split by the period's runs,
 * the same numbers as text beside it, and the period in one line. It replaces the
 * page's figure row: the share answers the question the three figures only hinted at.
 *
 * The bar is a picture of the legend, not a replacement for it (research R1): every
 * value is also written out. Runs are a quantity, so the segments are drawn as one
 * (spec 251): the leading profile in brand blue, the rest in grey steps, the unknown
 * faintest. Each profile's own colour stays on its dot in the legend, beside its name.
 */
export function RunsShareCard({
  entries,
  period,
  identities,
}: {
  entries: AIModelEntry[];
  period: PeriodBounds;
  identities: Map<string, ProfileIdentity>;
}) {
  const headingId = useId();
  const segments = shareSegments(entries, identities);
  const fills = segments.map((segment) => quantityFill(segment.rank));
  return (
    <section
      aria-labelledby={headingId}
      className="border-border bg-card flex min-w-0 flex-col gap-3 rounded-lg border p-4"
    >
      <div className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4">
        <SectionHeading id={headingId}>Runs by profile</SectionHeading>
        <p className="text-muted-foreground text-xs tabular-nums">
          {summaryLine(entries, period)}
        </p>
      </div>
      {segments.length > 0 ? (
        <>
          {/* The shared stacked bar (spec 234 US7), as Token details draws its mix;
              the legend below is this card's own, with runs and shares. */}
          <CompositionBar
            segments={segments.map((segment, index) => ({
              key: segment.key,
              label: segment.name,
              fill: fills[index],
              value: segment.runs,
            }))}
            total={segments.reduce((sum, segment) => sum + segment.runs, 0)}
            description={segments
              .map(
                (s) =>
                  `${s.name}, ${String(s.runs)} ${s.runs === 1 ? 'run' : 'runs'}, ${String(s.percent)} percent`,
              )
              .join('; ')}
            legend={false}
          />
          <ul
            aria-label="Runs by profile"
            className="flex flex-wrap gap-x-5 gap-y-1.5 text-sm"
          >
            {segments.map((segment) => (
              <li key={segment.key} className="flex items-center gap-1.5">
                <span
                  aria-hidden="true"
                  data-identity-dot=""
                  className={cn(
                    'size-2 shrink-0 rounded-full',
                    identityDot(segment.identity),
                  )}
                />
                <span>{segment.name}</span>
                <span className="font-semibold tabular-nums">
                  {segment.runs}
                </span>
                <span className="text-muted-foreground tabular-nums">
                  {`${String(segment.percent)}%`}
                </span>
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </section>
  );
}
